import { reactive } from "vue";

export type PendingNodeJobId = string | number;

export const CreatedAicStammTable: PendingNodeJobId[] = reactive([]);

export type PendingNodeJobTracker = {
  jobId: string | number;
  title: string;
  message: string;
  ids: Array<string | number>;
  nodeRequest: string;
  statusPath: string;
  urlEndpoint: string;
};

export type PendingNodeJobStatusResult = {
  aic: PendingNodeJobId;
  status: string;
  pending: boolean;
};

const NODE_BACKGROUND_JOB_POLL_MS = 3000;

type ApplyNodeJobStatusUpdate = {
  status: string;
  progress?: number | null;
  jobId?: string | number | null;
  ids?: Array<string | number | undefined | null>;
  title?: string | null;
  message?: string | null;
};

type NodeRestOperation = "create" | "update" | "delete" | "get";

type PendingNodeJobManagerDeps = {
  sendRestNodeRequest: (
    nodeRequest: string,
    operation: NodeRestOperation,
    body?: any,
    urlEndpoint?: string,
  ) => Promise<any>;
  applyNodeJobStatus: (update: ApplyNodeJobStatusUpdate) => { status?: string } | undefined;
  getDefaultUrlEndpoint: () => string;
};

export function normalizePendingNodeJobKey(jobId: PendingNodeJobId) {
  return String(jobId);
}

export function normalizePendingNodeJobStatus(value: unknown, payload: Record<string, any>) {
  const status = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (status.length > 0) {
    switch (status) {
      case "done":
      case "completed":
      case "success":
      case "ok":
      case "complete":
      case "fertig":
      case "beendet":
      case "abgeschlossen":
      case "erfolgreich":
        return "success";
      case "failed":
      case "fehler":
        return "error";
      case "cancelled":
      case "canceled":
      case "abgebrochen":
        return "cancelled";
      default:
        return status;
    }
  }
  const infoText = String(
    payload?.jobStatus?.message
    ?? payload?.jobStatus?.info
    ?? payload?.jobStatus?.detail
    ?? payload?.info
    ?? payload?.message
    ?? payload?.detail
    ?? "",
  ).trim().toLowerCase();
  if (
    infoText.includes("fertig")
    || infoText.includes("abgeschlossen")
    || infoText.includes("completed")
    || infoText.includes("done")
    || infoText.includes("success")
    || infoText.includes("erfolgreich")
  ) {
    return "success";
  }
  if (infoText.includes("fehler") || infoText.includes("error")) {
    return "error";
  }
  if (payload?.cancelled === true || payload?.canceled === true) {
    return "cancelled";
  }
  if (payload?.error || payload?.fehler) {
    return "error";
  }
  if (
    payload?.completed === true
    || payload?.done === true
    || payload?.finished === true
    || payload?.success === true
  ) {
    return "success";
  }
  if (payload?.queued === true || payload?.pending === true) {
    return "queued";
  }
  return "running";
}

function parsePendingNodeJobProgress(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function extractPendingNodeJobPayload(response: any) {
  const parseJsonString = (value: unknown) => {
    if (typeof value !== "string") {
      return value;
    }
    const trimmedPayload = value.trim();
    if (!trimmedPayload.startsWith("{") && !trimmedPayload.startsWith("[")) {
      return value;
    }
    try {
      return JSON.parse(trimmedPayload);
    } catch (error) {
      console.warn("PendingNodeJobStatus JSON konnte nicht geparst werden", error);
      return value;
    }
  };

  const unwrap = (input: unknown, depth: number = 0): Record<string, any> | null => {
    if (depth > 4) {
      return null;
    }
    const parsed = parseJsonString(input);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const record = parsed as Record<string, any>;
    const hasStatusField =
      typeof record.status === "string"
      || typeof record.state === "string"
      || typeof record.jobState === "string"
      || typeof record.phase === "string"
      || typeof record?.jobStatus?.status === "string";
    if (hasStatusField) {
      return record;
    }
    if (record.data !== undefined) {
      const nested = unwrap(record.data, depth + 1);
      if (nested) {
        return nested;
      }
    }
    if (record.payload !== undefined) {
      const nested = unwrap(record.payload, depth + 1);
      if (nested) {
        return nested;
      }
    }
    if (record.result !== undefined) {
      const nested = unwrap(record.result, depth + 1);
      if (nested) {
        return nested;
      }
    }
    return record;
  };

  return unwrap(response?.data) ?? unwrap(response) ?? {};
}

export function extractPendingNodeJobStatus(response: any) {
  const safePayload = extractPendingNodeJobPayload(response);
  let status = normalizePendingNodeJobStatus(
    safePayload?.jobStatus?.status
    ?? safePayload?.status
    ?? safePayload?.state
    ?? safePayload?.jobState
    ?? safePayload?.phase,
    safePayload,
  );
  const progress = parsePendingNodeJobProgress(
    safePayload?.jobStatus?.progress
    ?? safePayload?.progress
    ?? safePayload?.percent
    ?? safePayload?.percentage
    ?? safePayload?.prozent
    ?? safePayload?.fortschritt,
  );
  const messageValue = safePayload?.jobStatus?.message
    ?? safePayload?.message
    ?? safePayload?.info
    ?? safePayload?.error
    ?? safePayload?.fehler
    ?? safePayload?.detail;
  if ((status === "running" || status === "queued" || status === "unknown") && progress === 100) {
    status = "success";
  }
  return {
    status,
    progress,
    message: messageValue === undefined || messageValue === null
      ? null
      : String(messageValue).trim() || null,
  };
}

export function isPendingNodeJobTerminal(status: string) {
  switch ((status ?? "").toLowerCase()) {
    case "success":
    case "done":
    case "completed":
    case "error":
    case "failed":
    case "cancelled":
    case "canceled":
      return true;
    default:
      return false;
  }
}

export class NodeJobStatusManager {
  private readonly pendingNodeJobs = new Map<string, PendingNodeJobTracker>();
  private readonly pendingNodeJobPollTimers = new Map<string, ReturnType<typeof setTimeout>>();

  public constructor(private readonly deps: PendingNodeJobManagerDeps) {}

  private clearPendingNodeJobPollTimer(jobId: PendingNodeJobId) {
    const key = normalizePendingNodeJobKey(jobId);
    const timer = this.pendingNodeJobPollTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.pendingNodeJobPollTimers.delete(key);
    }
  }

  private removeCreatedNodeJob(jobId: PendingNodeJobId) {
    const key = normalizePendingNodeJobKey(jobId);
    const idx = CreatedAicStammTable.findIndex((value) => normalizePendingNodeJobKey(value) === key);
    if (idx !== -1) {
      CreatedAicStammTable.splice(idx, 1);
    }
  }

  private clearPendingNodeJob(jobId: PendingNodeJobId) {
    const key = normalizePendingNodeJobKey(jobId);
    this.clearPendingNodeJobPollTimer(jobId);
    this.pendingNodeJobs.delete(key);
    this.removeCreatedNodeJob(jobId);
  }

  private getPendingNodeJobTracker(jobId: PendingNodeJobId) {
    const key = normalizePendingNodeJobKey(jobId);
    const existing = this.pendingNodeJobs.get(key);
    if (existing) {
      return existing;
    }
    const fallback: PendingNodeJobTracker = {
      jobId: `background-job:${key}`,
      title: `Job ${key}`,
      message: "Wird im Hintergrund verarbeitet",
      ids: [jobId],
      nodeRequest: "reisekosten/beleg",
      statusPath: "status",
      urlEndpoint: this.deps.getDefaultUrlEndpoint(),
    };
    this.pendingNodeJobs.set(key, fallback);
    return fallback;
  }

  private schedulePendingNodeJobPoll(jobId: PendingNodeJobId, delayMs: number = NODE_BACKGROUND_JOB_POLL_MS) {
    const key = normalizePendingNodeJobKey(jobId);
    this.clearPendingNodeJobPollTimer(jobId);
    if (!this.pendingNodeJobs.has(key)) {
      return;
    }
    this.pendingNodeJobPollTimers.set(
      key,
      setTimeout(() => {
        this.syncPendingNodeJob(jobId).catch((error) => {
          console.error("Fehler beim Hintergrundjob-Status:", error);
        });
      }, delayMs),
    );
  }

  public startPendingNodeJobTracking(jobId: PendingNodeJobId, tracker: PendingNodeJobTracker) {
    const key = normalizePendingNodeJobKey(jobId);
    const ids = [jobId, ...tracker.ids]
      .filter((value): value is string | number => value !== undefined && value !== null && value !== "")
      .filter(
        (value, index, array) =>
          array.findIndex((candidate) => normalizePendingNodeJobKey(candidate) === normalizePendingNodeJobKey(value)) === index,
      );
    this.pendingNodeJobs.set(key, {
      ...tracker,
      ids,
    });
    if (!CreatedAicStammTable.some((value) => normalizePendingNodeJobKey(value) === key)) {
      CreatedAicStammTable.push(jobId);
    }
    this.deps.applyNodeJobStatus({
      status: "running",
      progress: null,
      jobId: tracker.jobId,
      ids,
      title: tracker.title,
      message: "Wird im Hintergrund weiterverarbeitet",
    });
    this.schedulePendingNodeJobPoll(jobId);
  }

  public ensurePendingNodeJobPolling() {
    for (const jobId of [...CreatedAicStammTable]) {
      const key = normalizePendingNodeJobKey(jobId);
      if (!this.pendingNodeJobPollTimers.has(key)) {
        this.schedulePendingNodeJobPoll(jobId);
      }
    }
  }

  public async syncPendingNodeJob(jobId: PendingNodeJobId): Promise<PendingNodeJobStatusResult> {
    const tracker = this.getPendingNodeJobTracker(jobId);
    this.clearPendingNodeJobPollTimer(jobId);
    try {
      const response = await this.deps.sendRestNodeRequest(
        tracker.nodeRequest,
        "get",
        {
          id: jobId,
          statusPath: tracker.statusPath,
        },
        tracker.urlEndpoint,
      );
      const statusUpdate = extractPendingNodeJobStatus(response);
      const appliedStatus = this.deps.applyNodeJobStatus({
        status: statusUpdate.status,
        progress: statusUpdate.progress,
        jobId: tracker.jobId,
        ids: tracker.ids,
        title: tracker.title,
        message: statusUpdate.message ?? tracker.message,
      });

      if (isPendingNodeJobTerminal(appliedStatus?.status ?? statusUpdate.status)) {
        this.clearPendingNodeJob(jobId);
        return {
          aic: jobId,
          status: appliedStatus?.status ?? statusUpdate.status,
          pending: false,
        };
      }

      this.schedulePendingNodeJobPoll(jobId);
      return {
        aic: jobId,
        status: appliedStatus?.status ?? statusUpdate.status,
        pending: true,
      };
    } catch (error: any) {
      this.deps.applyNodeJobStatus({
        status: "error",
        progress: 0,
        jobId: tracker.jobId,
        ids: tracker.ids,
        title: tracker.title,
        message: error?.response?.data?.info ?? error?.message ?? tracker.message,
      });
      this.clearPendingNodeJob(jobId);
      throw error;
    }
  }

  public async syncAllPendingNodeJobs() {
    const pendingJobs = [...CreatedAicStammTable];
    const results = await Promise.allSettled(
      pendingJobs.map((jobId) => this.syncPendingNodeJob(jobId)),
    );
    return {
      checked: pendingJobs.length,
      completed: results.filter(
        (result) => result.status === "fulfilled" && result.value.pending === false,
      ).length,
      failed: results.filter((result) => result.status === "rejected").length,
      remaining: CreatedAicStammTable.length,
    };
  }
}